/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { InputAdapterService } from '@api/services/input-adapter/input-adapter.service';
import { OutputAdapterService } from '@api/services/output-adapter/output-adapter.service';
import { map } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import {
  ApiParams,
  QuotationBoundariesDTO,
  DomainListDTO,
  DigitalSignStartDTO,
  DigitalSignVerifyDTO,
  ChannelDTO
} from '@api/models';
import { HttpReqService } from '@core/modules/api/services/http-req/http-req.service';

@Injectable()
export class ApiCustomService extends ApiService {
  constructor(
    protected http: HttpClient,
    protected outputAdapter: OutputAdapterService,
    protected inputAdapter: InputAdapterService,
    protected httpReqService: HttpReqService
  ) {
    super(http, outputAdapter, inputAdapter, httpReqService);
  }

  postPositionWithdraw(
    params: ApiParams<{ positionId: string }, { reasonCode: string }>
  ): Observable<any> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/WITHDRAW_REQUEST`;

    return this.http
      .post<any>(
        url,
        this.inputAdapter.postPositionWithdraw(params).body,
        this.inputAdapter.postPositionWithdraw(params).options
      )
      .pipe(map(res => this.outputAdapter.postPositionWithdraw(res)));
  }

  postSignStart(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignStartDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/request-dsp`;

    return this.http
      .get<DigitalSignStartDTO>(
        url,
        this.inputAdapter.postSignStart(params).options
      )
      .pipe(map(res => this.outputAdapter.postSignStart(res)));
  }

  getSignVerify(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignVerifyDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/request-status-dsp`;

    return this.http
      .get<DigitalSignVerifyDTO>(
        url,
        this.inputAdapter.getSignVerify(params).options
      )
      .pipe(map(res => this.outputAdapter.getSignVerify(res)));
  }

  getSignResume(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<DigitalSignStartDTO> {
    const url = `${this.apiUrl}/v1/positions/${params.pathParams.positionId}/digital-signature/sign`;

    return this.http
      .get<DigitalSignStartDTO>(
        url,
        this.inputAdapter.getSignResume(params).options
      )
      .pipe(map(res => this.outputAdapter.getSignResume(res)));
  }

  getPositionQuotationBoundaries(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<QuotationBoundariesDTO> {
    const url = `${this.apiUrl}/v1/quotations/calculate/revolving/credituse/range?positionId=${params.pathParams.positionId}`;

    return forkJoin([
      this.getDomain({ pathParams: { code: 'MAX_DURATION' } }),
      this.http.get<{
        payload: {
          minRequestable: number;
          maxRequestable: number;
          minDuration: number;
          maxDuration: number;
        };
      }>(url, params.options)
    ]).pipe(
      map(res => {
        const durations = res[0];
        const range = res[1];

        return {
          financedMaxAmount: range.payload.maxRequestable,
          financedMinAmount: range.payload.minRequestable,
          instalmentMaxAmount:
            range.payload.maxRequestable / range.payload.minDuration,
          instalmentMinAmount: null,
          durationMin: range.payload.minDuration,
          durationMax: range.payload.maxDuration,
          durationSteps: this.calcDurationStep(durations),
          amountSteps: 100,
          instalmentSteps: 10,
          purposes: [{ description: 'CQS', code: 'CQS' }]
        };
      }),
      map(res => this.outputAdapter.getPositionQuotationBoundaries(res))
    );
  }

  getChannel(
    params: ApiParams<{ positionId: string }, void>
  ): Observable<ChannelDTO> {
    return of(null);
  }

  private calcDurationStep(domain: DomainListDTO): number {
    const durations = domain.domainList
      .sort((a, b) => Number(a.key) - Number(b.key))
      .filter(
        (item, index, arr) => arr.findIndex(x => x.key === item.key) === index
      );

    const first =
      durations[0] && durations[0].key ? Number(durations[0].key) : 0;
    const second =
      durations[1] && durations[1].key ? Number(durations[1].key) : 0;
    const diff = second - first;

    return diff <= 0 ? 1 : diff;
  }
}
